import { Box, Button, Checkbox, FormControlLabel, Grid, Tab, useTheme } from '@mui/material'
import InputMask from 'components/FormInputMask'
import { InputMultiSelect } from 'components/FormInputMultiSelect'
import { FormInputSelect } from 'components/FormInputSelect'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import React, { useEffect } from 'react'
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form'
import {
  PendingProcessHearingFilterDefaultValues,
  PendingProcessHearingFilterSchema,
  PendingProcessHearingFilterType,
} from 'types/pending-process-hearing.type'
import { zodResolver } from '@hookform/resolvers/zod'
import { PromotoriaType } from 'contexts/promotoria.context'
import { StyledTabs } from 'pages/PendingProcessHearing/form/PendingProcessHearingForm/style'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import TuneIcon from '@mui/icons-material/Tune'
import { FormInputAutoComplete } from 'components/FormInputAutoComplete'

const promotoriasCapital = [
  {
    id: 300592,
    descricao: '1ª PROMOTORIA DE JUSTIÇA DA INFÂNCIA E DA JUVENTUDE INFRACIONAL DA CAPITAL',
  },
  {
    id: 100870,
    descricao: '2ª PROMOTORIA DE JUSTIÇA DA INFÂNCIA E DA JUVENTUDE INFRACIONAL DA CAPITAL',
  },
  {
    id: 100871,
    descricao: '3ª PROMOTORIA DE JUSTIÇA DA INFÂNCIA E DA JUVENTUDE INFRACIONAL DA CAPITAL',
  },
  {
    id: 100872,
    descricao: '4ª PROMOTORIA DE JUSTIÇA DA INFÂNCIA E DA JUVENTUDE INFRACIONAL DA CAPITAL',
  },
  {
    id: 29531499,
    descricao: '5ª PROMOTORIA DE JUSTIÇA DA INFÂNCIA E DA JUVENTUDE INFRACIONAL DA CAPITAL',
  },
  {
    id: 47109910,
    descricao: '6ª PROMOTORIA DE JUSTIÇA DA INFÂNCIA E DA JUVENTUDE INFRACIONAL DA CAPITAL',
  },
]

type PendingProcessHearingFormProps = {
  promotoriaSelecionada?: PromotoriaType | null
  onBuscarCallback: (props: PendingProcessHearingFilterType, resetPage: boolean) => Promise<void>
  resetData: () => void
}

export const PendingProcessHearingForm = ({
  onBuscarCallback,
  promotoriaSelecionada,
  resetData,
}: PendingProcessHearingFormProps) => {
  const theme = useTheme()

  const form = useForm<PendingProcessHearingFilterType>({
    resolver: zodResolver(PendingProcessHearingFilterSchema),
    defaultValues: PendingProcessHearingFilterDefaultValues,
  })

  const { control, handleSubmit, reset, setValue, watch } = form

  // Monitora a aba ativa
  const tipoTabWatch = watch('tipo')

  // Manipulador para troca de aba
  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setValue('tipo', newValue) // Atualiza o RHF com a aba ativa
  }

  const onSubmit = async (props?: PendingProcessHearingFilterType) => {
    const promotoria =
      props?.tipo === 'AVANCADO' ? props.promotoria : String(promotoriaSelecionada?.id)
    if (!!props && !!promotoriaSelecionada) {
      await onBuscarCallback(
        {
          ...props,
          numeroCnj: props.numeroCnj?.replace(/\D/g, '') || null,
          status: props.status,
          categoria: props.categoria !== '0' ? props.categoria : null,
          promotoria,
        },
        true,
      )
    }
  }

  const isPromotoriaCapital = promotoriasCapital.some(
    (promotoriaCapital) => promotoriaCapital.id === promotoriaSelecionada?.id,
  )

  useEffect(() => {
    if (!isPromotoriaCapital) {
      setValue('tipo', 'NORMAL')
    }
  }, [isPromotoriaCapital])

  useEffect(() => {
    ;(async () => {
      if (promotoriaSelecionada) {
        await handleSubmit(async (data) => {
          const promotoria =
            data?.tipo === 'AVANCADO' ? data.promotoria : String(promotoriaSelecionada?.id)
          await onBuscarCallback(
            {
              ...data,
              categoria: data.categoria !== '0' ? data.categoria : null,
              promotoria,
            },
            true,
          )
        })()
      }
    })()
  }, [promotoriaSelecionada])

  useEffect(() => {
    ;(async () => {
      if (promotoriaSelecionada) {
        await handleSubmit(async (data) => {
          const promotoria =
            data?.tipo === 'AVANCADO' ? data.promotoria : String(promotoriaSelecionada?.id)
          await onBuscarCallback(
            {
              ...data,
              categoria: data.categoria !== '0' ? data.categoria : null,
              promotoria,
            },
            false,
          )
        })()
      }
    })()
  }, [onBuscarCallback])

  useEffect(() => {
    if (tipoTabWatch) {
      resetData()
    }
  }, [tipoTabWatch])

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container item spacing={2}>
          {isPromotoriaCapital && (
            <Grid item xs={12}>
              <Controller
                control={control}
                name={'tipo'}
                render={({ field }) => (
                  <Box color={theme.palette.text.primary}>
                    <StyledTabs
                      variant={'fullWidth'}
                      value={field.value} // O valor é controlado pelo RHF
                      onChange={handleTabChange}
                    >
                      <Tab
                        label='Filtro padrão'
                        value={'NORMAL'}
                        icon={<FilterAltIcon />}
                        iconPosition='start'
                      />
                      <Tab
                        label='Filtro avançado'
                        value={'AVANCADO'}
                        icon={
                          <Box display={'flex'} alignItems={'center'}>
                            <FilterAltIcon />
                            <TuneIcon
                              sx={{
                                height: '1.2rem',
                                width: '1.25rem',
                              }}
                            />
                          </Box>
                        }
                        iconPosition='start'
                      />
                    </StyledTabs>
                  </Box>
                )}
              />
            </Grid>
          )}
          {'NORMAL' === tipoTabWatch && <TabNormal />}
          {'AVANCADO' === tipoTabWatch && <TabAvancado />}
          <Grid item xs={12}>
            <Box display={'flex'} flexDirection={'row'}>
              <Button
                variant={'outlined'}
                color={'secondary'}
                sx={{ marginRight: '1rem' }}
                onClick={() =>
                  reset({ tipo: tipoTabWatch, status: ['PENDENTE'] }, { keepDirty: true })
                }
              >
                Limpar
              </Button>
              <Button
                type={'submit'}
                variant={'contained'}
                color={'secondary'}
                startIcon={<SearchRoundedIcon />}
              >
                Pesquisar
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  )
}

const TabNormal = () => {
  const { control } = useFormContext<PendingProcessHearingFilterType>()

  return (
    <Grid container item spacing={2}>
      <Grid item lg={4} xs={12}>
        <InputMask
          control={control}
          name={'numeroCnj'}
          label={'Número CNJ'}
          mask='0000000.00.0000.0.00.0000'
          placeholder='Informe o número CNJ'
        />
      </Grid>
      <Grid item lg={4} xs={12}>
        <InputMultiSelect
          label={'Status'}
          name={'status'}
          control={control}
          options={[
            { value: 'PENDENTE', label: 'Pendente' },
            { value: 'NAO_REALIZADO', label: 'Não Realizado' },
            { value: 'AGENDADO', label: 'Agendado' },
            { value: 'FINALIZADO', label: 'Finalizado' },
          ]}
        />
      </Grid>
      <Grid item lg={4} xs={12}>
        <FormInputSelect
          label={'Categoria'}
          name={'categoria'}
          control={control}
          options={[
            { label: 'AAAPAI', value: 'AAAPAI' },
            { label: 'AIAI', value: 'AIAI' },
            { label: 'BOC', value: 'BOC' },
          ]}
        />
      </Grid>
    </Grid>
  )
}

const TabAvancado = () => {
  const { control, watch } = useFormContext<PendingProcessHearingFilterType>()

  const excessaoWatch = watch('excessao')

  return (
    <Grid container item spacing={2}>
      {!excessaoWatch && (
        <Grid item xs={12}>
          <FormInputAutoComplete
            label={'Promotoria*'}
            name={'promotoria'}
            control={control}
            options={promotoriasCapital}
            getOptionLabel={(option) => option.descricao}
            getOptionById={(option: string, options) => {
              return options.find((item) => item.id === Number(option)) || null
            }}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            onChangeValue={(option) => String(option?.id) || null}
          />
        </Grid>
      )}
      {excessaoWatch && (
        <Grid item lg={4} xs={12}>
          <InputMask
            control={control}
            name={'numeroCnj'}
            label={'Número CNJ'}
            mask='0000000.00.0000.0.00.0000'
            placeholder='Informe o número CNJ'
          />
        </Grid>
      )}
      <Grid item lg={4} xs={12}>
        <InputMultiSelect
          label={'Status'}
          name={'status'}
          control={control}
          options={[
            { value: 'PENDENTE', label: 'Pendente' },
            { value: 'NAO_REALIZADO', label: 'Não Realizado' },
            { value: 'AGENDADO', label: 'Agendado' },
            { value: 'FINALIZADO', label: 'Finalizado' },
          ]}
        />
      </Grid>
      <Grid item lg={4} xs={12}>
        <FormInputSelect
          label={'Categoria'}
          name={'categoria'}
          control={control}
          options={[
            { label: 'AAAPAI', value: 'AAAPAI' },
            { label: 'AIAI', value: 'AIAI' },
            { label: 'BOC', value: 'BOC' },
          ]}
        />
      </Grid>
      <Grid item xs={12}>
        <Controller
          name='excessao'
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Checkbox
                  {...field}
                  color={'secondary'}
                  checked={field.value || false} // Garante a sincronização com o estado
                />
              }
              label='Exceção'
            />
          )}
        />
      </Grid>
    </Grid>
  )
}
